import React, { useState } from "react";
import { X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PromoCodeField from "@/components/PromoCodeField";
import { useConfig } from "@/lib/useConfig";

const EVENT_TIERS = [
  { key: "basic", name: "Basic Listing", price: 15, features: ["Standard placement in the events directory", "Listed in relevant category & region", "Live for 60 days", "Link to your event page"] },
  { key: "featured", name: "Featured Listing", price: 35, popular: true, features: ["Top placement in the events directory", "Featured badge & bold card", "Live for 60 days", "Included in one WorldAI Weekly newsletter"] },
];

const TYPES = ["Conference", "Summit", "Hackathon", "Webinar", "Meetup", "Workshop", "Competition"];
const inputCls = "w-full bg-[#111827] border border-white/10 rounded-md px-3 py-2.5 text-sm focus:border-[#00F0FF] focus:outline-none";

export default function SubmitEventModal({ open, onClose }) {
  const empty = { title: "", organizer: "", organizer_website: "", event_type: "Conference",
    location: "", city: "", country: "", start_date: "", end_date: "", is_virtual: false,
    is_free: false, price: "", expected_attendance: "",
    description: "", website: "", contact_email: "" };
  const [form, setForm] = useState(empty);
  const [step, setStep] = useState("tier");
  const [tier, setTier] = useState(null);
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);
  const { paymentsEnabled } = useConfig();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const effPrice = tier ? (promo ? +(tier.price * (1 - promo.discount_pct / 100)).toFixed(2) : tier.price) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/events/checkout", { form, tier: paymentsEnabled ? tier.key : "basic", promo_code: promo?.code, origin_url: window.location.origin });
      if (data.free || !data.url) {
        toast.success("Submitted! We'll review your event within 24 hours and email you when it's live — you're on the WorldAI Weekly too.");
        close();
      } else {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      setBusy(false);
    }
  };
  const close = () => { setForm(empty); setStep("tier"); setTier(null); setPromo(null); setBusy(false); onClose(); };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="submit-event-modal">
      <div className="card-surface rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-7 relative">
        <button onClick={close} className="absolute top-4 right-4 text-[#94A3B8] hover:text-white" data-testid="submit-event-close"><X className="w-5 h-5" /></button>
        {paymentsEnabled && step === "tier" ? (
          <div data-testid="submit-event-tiers">
            <h3 className="font-display font-bold text-2xl mb-1">Submit an Event</h3>
            <p className="text-sm text-[#94A3B8] mb-6">Choose a plan. Payment is required before your event enters the review queue.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {EVENT_TIERS.map((t) => (
                <div key={t.key} data-testid={`event-tier-card-${t.key}`}
                  className={`rounded-xl p-5 border flex flex-col ${t.popular ? "border-[#00F0FF]/50 bg-[#00F0FF]/5" : "border-white/10 bg-[#0e1424]"}`}>
                  {t.popular && <span className="font-mono-tech text-[10px] uppercase text-[#00F0FF] mb-2">Most popular</span>}
                  <h4 className="font-display font-bold text-lg">{t.name}</h4>
                  <div className="font-display font-extrabold text-3xl text-[#00F0FF] my-2">${t.price}</div>
                  <ul className="space-y-1.5 text-xs text-[#94A3B8] flex-1 mb-4">
                    {t.features.map((f, i) => <li key={i} className="flex gap-1.5"><span className="text-[#22C55E]">✓</span> {f}</li>)}
                  </ul>
                  <button onClick={() => { setTier(t); setStep("form"); }} data-testid={`event-tier-select-${t.key}`}
                    className={`w-full font-semibold px-4 py-2.5 rounded-md transition text-sm ${t.popular ? "bg-[#00F0FF] text-[#04121a] hover:brightness-110" : "border border-white/15 hover:border-[#00F0FF]/60"}`}>
                    Choose {t.name.split(" ")[0]}
                  </button>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <h3 className="font-display font-bold text-2xl">Submit an Event</h3>
              {paymentsEnabled && (
                <button onClick={() => { setStep("tier"); setPromo(null); }} data-testid="se-change-tier" className="text-xs text-[#00F0FF] hover:underline">← Change plan</button>
              )}
            </div>
            <p className="text-sm text-[#94A3B8] mb-6">
              {paymentsEnabled && tier ? (
                <>
                  <span className="text-white font-medium">
                    {tier.name} — {promo ? (<><span className="line-through text-[#64748B]">${tier.price}</span> <span className="text-[#FBBF24]">${effPrice}</span></>) : <>${tier.price}</>}.
                  </span> Your event will be reviewed and published within 24 hours after payment.
                </>
              ) : (
                <><span className="text-[#4ade80] font-medium">Free to list.</span> Every event is reviewed by our team and published within 24 hours — no payment required.</>
              )}
            </p>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Event Title *</label>
                <input required value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="se-title" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Organizer *</label>
                <input required value={form.organizer} onChange={(e) => set("organizer", e.target.value)} className={inputCls} data-testid="se-organizer" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Organizer Website</label>
                <input value={form.organizer_website} onChange={(e) => set("organizer_website", e.target.value)} className={inputCls} placeholder="https://" data-testid="se-org-website" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Event Type</label>
                <select value={form.event_type} onChange={(e) => set("event_type", e.target.value)} className={inputCls} data-testid="se-type">
                  {TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" id="se-virtual" checked={form.is_virtual} onChange={(e) => set("is_virtual", e.target.checked)} className="w-4 h-4 accent-[#00F0FF]" data-testid="se-virtual" />
                <label htmlFor="se-virtual" className="text-sm text-[#94A3B8]">Virtual event</label>
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">City</label>
                <input value={form.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="se-city" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Country</label>
                <input value={form.country} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="se-country" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Start Date</label>
                <input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className={inputCls} data-testid="se-start" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">End Date</label>
                <input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className={inputCls} data-testid="se-end" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Ticket Price</label>
                <input value={form.price} onChange={(e) => set("price", e.target.value)} className={inputCls} placeholder="e.g. From $99" disabled={form.is_free} data-testid="se-price" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Expected Attendance</label>
                <input value={form.expected_attendance} onChange={(e) => set("expected_attendance", e.target.value)} className={inputCls} placeholder="e.g. 2000" data-testid="se-attendance" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="se-free" checked={form.is_free} onChange={(e) => set("is_free", e.target.checked)} className="w-4 h-4 accent-[#22C55E]" data-testid="se-free" />
                <label htmlFor="se-free" className="text-sm text-[#94A3B8]">Free event</label>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-[#94A3B8] mb-1 block">Description *</label>
                <textarea required rows={4} value={form.description} onChange={(e) => set("description", e.target.value)} className={inputCls} data-testid="se-description" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Event Website *</label>
                <input required value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://" data-testid="se-website" />
              </div>
              <div>
                <label className="text-xs text-[#94A3B8] mb-1 block">Contact Email *</label>
                <input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className={inputCls} data-testid="se-contact-email" />
              </div>
              <div className="md:col-span-2 mt-2">
                <PromoCodeField onApplied={setPromo} />
              </div>
              <div className="md:col-span-2 mt-2">
                <button type="submit" disabled={busy}
                  className="w-full bg-[#00F0FF] text-[#04121a] font-semibold px-6 py-3 rounded-md hover:brightness-110 transition disabled:opacity-50" data-testid="se-submit">
                  {busy ? (paymentsEnabled ? "Redirecting to secure payment…" : "Submitting…") : (paymentsEnabled ? `Continue to Payment — $${effPrice}` : "Submit Event for Review")}
                </button>
                <p className="text-xs text-[#64748B] text-center mt-2">
                  {paymentsEnabled ? "Secure payment via Stripe. Your event is reviewed after payment is confirmed." : "Free while we're in pre-launch. Every event is reviewed before it goes live."}
                </p>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
